import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { VioTablesV2Component } from 'vio-tables-v2';
import type { VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { ApiRequestsService } from '../../services/api-requests.service';

declare const bootstrap: any;

interface SavedCampaign {
    _id: string;
    campaignName: string;
    templateName: string;
    templateType: string;
    outerVariable: string;
    cardVariables: string[];
    createdAt: string;
}

interface RecipientList {
    _id: string;
    listName: string;
    customerCount: number;
    users: Customer[];
    createdAt: string;
}

interface Customer {
    _id: string;
    name: string;
    whatsAppNo: string;
}

interface CampaignRun {
    _id: string;
    campaignId: string;
    campaignName: string;
    templateName: string;
    templateType: string;
    customers: Customer[];
    sentCount: number;
    failedCount: number;
    failedNumbers: string[];
    status: string;
    triggeredAt: string;
}

interface CampaignPreview {
    outerBodyText: string;
    cardBodyTexts: string[];
    buttonNames: string[];
    cardButtonNames: string[][] | null;
    cardImages: (string | null)[];
    cardCount: number;
    outerVariable: string;
    cardVariables: string[];
}

interface DeliveryData {
    summary: { total: number; sent: number; delivered: number; read: number; failed: number };
    logs: any[];
}

interface EngagementData {
    summary: { total: number; interested: number; notInterested: number };
    logs: any[];
}

@Component({
    selector: 'app-trigger-campaign',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule, VioTablesV2Component],
    templateUrl: './trigger-campaign.component.html',
    styleUrl: './trigger-campaign.component.scss',
})
export class TriggerCampaignComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('deliveryModal') deliveryModalRef?: ElementRef<HTMLElement>;
    @ViewChild('engagementModal') engagementModalRef?: ElementRef<HTMLElement>;
    @ViewChild('recipientsModal') recipientsModalRef?: ElementRef<HTMLElement>;

    // Campaign selector
    savedCampaigns: SavedCampaign[] = [];
    isLoadingCampaigns = false;
    selectedCampaignId = '';
    selectedCampaign: SavedCampaign | null = null;

    // Campaign preview
    campaignPreview: CampaignPreview | null = null;
    isLoadingPreview = false;
    previewError = '';

    // Recipient list selector
    recipientLists: RecipientList[] = [];
    isLoadingLists = false;
    selectedListId = '';
    selectedList: RecipientList | null = null;

    // Recipient mode: group list OR individual user pick
    recipientMode: 'group' | 'users' = 'group';
    allUsers: any[] = [];
    isLoadingUsers = false;
    userSearch = '';
    selectedUserIds = new Set<string>();

    get filteredUsers(): any[] {
        const q = this.userSearch.trim().toLowerCase();
        if (!q) return this.allUsers;
        return this.allUsers.filter(u =>
            (u.name || u.firstName || '').toLowerCase().includes(q) ||
            (u.whatsAppNo || '').includes(q)
        );
    }

    get selectedUsersCount(): number { return this.selectedUserIds.size; }

    toggleUser(id: string): void {
        if (this.selectedUserIds.has(id)) this.selectedUserIds.delete(id);
        else this.selectedUserIds.add(id);
    }

    toggleAllFiltered(): void {
        const filtered = this.filteredUsers;
        const allSelected = filtered.every(u => this.selectedUserIds.has(u._id));
        if (allSelected) filtered.forEach(u => this.selectedUserIds.delete(u._id));
        else filtered.forEach(u => this.selectedUserIds.add(u._id));
    }

    get allFilteredSelected(): boolean {
        const f = this.filteredUsers;
        return f.length > 0 && f.every(u => this.selectedUserIds.has(u._id));
    }

    onRecipientModeChange(): void {
        if (this.recipientMode === 'users' && !this.allUsers.length && !this.isLoadingUsers) {
            this.loadAllUsers();
        }
    }

    // Trigger state
    isTriggering = false;
    resultMessage = '';
    resultSuccess = false;

    // Sample user for preview
    sampleUser: any = null;

    // Runs table
    runs: CampaignRun[] = [];
    runsForTable: any[] = [];
    isLoadingRuns = false;

    // Modals
    deliveryData: DeliveryData | null = null;
    isLoadingDelivery = false;
    deliveryRunName = '';

    engagementData: EngagementData | null = null;
    isLoadingEngagement = false;
    engagementRunName = '';

    recipientsRunName = '';
    recipientsData: Customer[] = [];

    runsTableConfig: VT2_TABLE_CONFIG = {
        tableTitle: '',
        columnLabels: {
            campaignName: 'Campaign',
            templateName: 'Template',
            customers: 'Recipients',
            sentCount: 'Sent',
            failedCount: 'Failed',
            status: 'Status',
            triggeredAt: 'Triggered',
        },
        excludeFields: ['_id', 'campaignId', 'rawCustomers'],
        isSortable: true,
        showFormulaButton: false,
        showSummationButton: false,
        showHeaderGroupButton: false,
        dateFields: ['triggeredAt'],
        statusField: 'status',
        statusClassMap: {
            completed: 'bg-success',
            failed: 'bg-danger',
            draft: 'bg-secondary',
        },
    };

    private readonly STATUS_LABEL: Record<number, string> = { 10: 'Failed', 20: 'Sent', 30: 'Delivered', 40: 'Seen' };
    statusLabel(code: number): string { return this.STATUS_LABEL[code] ?? 'Unknown'; }

    trackByIndex(index: number): number { return index; }

    constructor(private apiService: ApiRequestsService) {}

    ngOnInit(): void {
        this.runsTableConfig = {
            ...this.runsTableConfig,
            actionButtons: [
                {
                    icon: '📊',
                    label: 'Delivery',
                    title: 'View delivery metrics',
                    onClick: (row) => this.fetchDelivery(row._id, row.campaignName),
                },
                {
                    icon: '👆',
                    label: 'Engagement',
                    title: 'View button engagement',
                    onClick: (row) => this.fetchEngagement(row._id, row.campaignName),
                },
                {
                    icon: '👥',
                    label: 'Recipients',
                    title: 'Show recipients',
                    onClick: (row) => this.showRecipients(row),
                },
            ],
        };

        this.loadSavedCampaigns();
        this.loadRecipientLists();
        this.loadRuns();
        this.loadSampleUser();
    }

    ngAfterViewInit(): void {
        if (this.deliveryModalRef) document.body.appendChild(this.deliveryModalRef.nativeElement);
        if (this.engagementModalRef) document.body.appendChild(this.engagementModalRef.nativeElement);
        if (this.recipientsModalRef) document.body.appendChild(this.recipientsModalRef.nativeElement);
    }

    ngOnDestroy(): void {
        [this.deliveryModalRef, this.engagementModalRef, this.recipientsModalRef].forEach(ref => {
            const el = ref?.nativeElement;
            if (el?.parentNode === document.body) document.body.removeChild(el);
        });
    }

    private readonly KNOWN_USER_FIELDS = new Set(['name', 'firstName', 'lastName', 'organisationName', 'empNo', 'whatsAppNo']);

    loadSampleUser(): void {
        this.apiService.getApiCall('api/v1/user-master').subscribe({
            next: (res: any) => {
                const users: any[] = Array.isArray(res?.Data) ? res.Data : [];
                this.sampleUser = users.find(u => u.firstName?.trim() || u.name?.trim() || u.lastName?.trim()) ?? users[0] ?? null;
            },
            error: () => {},
        });
    }

    loadAllUsers(): void {
        this.isLoadingUsers = true;
        this.apiService.getApiCall('api/v1/user-master').subscribe({
            next: (res: any) => {
                this.isLoadingUsers = false;
                this.allUsers = Array.isArray(res?.Data) ? res.Data : [];
            },
            error: () => { this.isLoadingUsers = false; },
        });
    }

    loadSavedCampaigns(): void {
        this.isLoadingCampaigns = true;
        this.apiService.getApiCall('api/v1/campaign/list').subscribe({
            next: (res: any) => {
                this.isLoadingCampaigns = false;
                this.savedCampaigns = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
            },
            error: () => { this.isLoadingCampaigns = false; },
        });
    }

    loadRecipientLists(): void {
        this.isLoadingLists = true;
        this.apiService.getApiCall('api/v1/recipient-lists').subscribe({
            next: (res: any) => {
                this.isLoadingLists = false;
                this.recipientLists = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
            },
            error: () => { this.isLoadingLists = false; },
        });
    }

    loadRuns(): void {
        this.isLoadingRuns = true;
        this.apiService.getApiCall('api/v1/campaign/runs').subscribe({
            next: (res: any) => {
                this.isLoadingRuns = false;
                this.runs = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
                this.runsForTable = this.runs.map(r => ({
                    _id: r._id,
                    campaignId: r.campaignId,
                    campaignName: r.campaignName,
                    templateName: r.templateName || '—',
                    customers: r.customers?.length ?? 0,
                    rawCustomers: r.customers || [],
                    sentCount: r.sentCount,
                    failedCount: r.failedCount,
                    status: r.status,
                    triggeredAt: r.triggeredAt,
                }));
            },
            error: () => { this.isLoadingRuns = false; },
        });
    }

    onCampaignSelect(campaignId: string): void {
        this.selectedCampaignId = campaignId;
        this.selectedCampaign = this.savedCampaigns.find(c => c._id === campaignId) || null;
        this.campaignPreview = null;
        this.previewError = '';
        if (campaignId) this.loadCampaignPreview(campaignId);
    }

    loadCampaignPreview(id: string): void {
        this.isLoadingPreview = true;
        this.apiService.getApiCall(`api/v1/campaign/${id}/preview`).subscribe({
            next: (res: any) => {
                this.isLoadingPreview = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.campaignPreview = res.Data;
                } else {
                    this.previewError = res?.errorDescription || 'Failed to load preview.';
                }
            },
            error: (err: any) => {
                this.isLoadingPreview = false;
                this.previewError = err?.error?.errorDescription || err?.message || 'Preview API error.';
            },
        });
    }

    onListSelect(listId: string): void {
        this.selectedListId = listId;
        this.selectedList = this.recipientLists.find(l => l._id === listId) || null;
    }

    previewText(text: string, varValue?: string): string {
        let result = text;
        if (varValue?.trim()) {
            const parts = varValue.split(',').map(v => v.trim());
            parts.forEach((val, i) => {
                const resolved = (this.KNOWN_USER_FIELDS.has(val) && this.sampleUser)
                    ? (String(this.sampleUser[val] ?? '').trim() || val)
                    : val;
                result = result.replace(new RegExp(`\\{\\{${i + 1}\\}\\}`, 'g'), resolved);
            });
        }
        result = result.replace(/\{\{\d+\}\}/g, '[Var]');
        return result
            .replace(/\*([^*\n]+)\*/g, '$1')
            .replace(/_([^_\n]+)_/g, '$1')
            .replace(/~([^~\n]+)~/g, '$1');
    }

    previewButtons(cardIndex: number): string[] {
        if (!this.campaignPreview) return [];
        return this.campaignPreview.cardButtonNames?.[cardIndex] ?? this.campaignPreview.buttonNames;
    }

    get isValid(): boolean {
        if (!this.selectedCampaign) return false;
        if (this.recipientMode === 'group') return !!this.selectedList;
        return this.selectedUserIds.size > 0;
    }

    get validationHint(): string {
        if (!this.selectedCampaign) return 'Select a campaign to trigger.';
        if (this.recipientMode === 'group' && !this.selectedList) return 'Select a recipient group.';
        if (this.recipientMode === 'users' && this.selectedUserIds.size === 0) return 'Select at least one recipient.';
        return '';
    }

    trigger(): void {
        if (!this.isValid || this.isTriggering) return;
        this.isTriggering = true;
        this.resultMessage = '';

        const fd = new FormData();
        fd.append('campaignId', this.selectedCampaign!._id);

        let recipientCount: number;
        if (this.recipientMode === 'group') {
            fd.append('recipientListId', this.selectedList!._id);
            recipientCount = this.selectedList!.customerCount;
        } else {
            const selected = this.allUsers
                .filter(u => this.selectedUserIds.has(u._id))
                .map(u => ({ _id: u._id, name: u.name || u.firstName || '', whatsAppNo: u.whatsAppNo }));
            fd.append('customers', JSON.stringify(selected));
            recipientCount = selected.length;
        }

        this.apiService.uploadMultiple(fd, 'api/v1/campaign/trigger').subscribe({
            next: (res: any) => {
                this.isTriggering = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    const { sentCount, failedCount, failedNumbers } = res.Data || {};
                    this.resultMessage = `Triggered! Sent to ${sentCount} of ${recipientCount} recipient(s).`
                        + (failedCount ? ` Failed: ${failedNumbers.join(', ')}` : '');
                    this.resultSuccess = true;
                    this.selectedCampaignId = '';
                    this.selectedCampaign = null;
                    this.selectedListId = '';
                    this.selectedList = null;
                    this.selectedUserIds.clear();
                    this.userSearch = '';
                    this.campaignPreview = null;
                    this.loadRuns();
                } else {
                    this.resultMessage = res?.errorDescription || 'Failed to trigger campaign.';
                    this.resultSuccess = false;
                }
            },
            error: () => {
                this.isTriggering = false;
                this.resultMessage = 'Failed to trigger campaign. Please check your connection.';
                this.resultSuccess = false;
            },
        });
    }

    fetchDelivery(runId: string, campaignName: string): void {
        this.deliveryData = null;
        this.deliveryRunName = campaignName;
        this.isLoadingDelivery = true;
        bootstrap.Modal.getOrCreateInstance(this.deliveryModalRef!.nativeElement).show();
        this.apiService.getApiCall(`api/v1/campaign/runs/${runId}/delivery`).subscribe({
            next: (res: any) => {
                this.isLoadingDelivery = false;
                if (res?.errorCode === this.apiService.NO_ERROR) this.deliveryData = res.Data;
            },
            error: () => { this.isLoadingDelivery = false; },
        });
    }

    fetchEngagement(runId: string, campaignName: string): void {
        this.engagementData = null;
        this.engagementRunName = campaignName;
        this.isLoadingEngagement = true;
        bootstrap.Modal.getOrCreateInstance(this.engagementModalRef!.nativeElement).show();
        this.apiService.getApiCall(`api/v1/campaign/runs/${runId}/engagement`).subscribe({
            next: (res: any) => {
                this.isLoadingEngagement = false;
                if (res?.errorCode === this.apiService.NO_ERROR) this.engagementData = res.Data;
            },
            error: () => { this.isLoadingEngagement = false; },
        });
    }

    showRecipients(row: any): void {
        this.recipientsRunName = row.campaignName;
        this.recipientsData = row.rawCustomers || [];
        bootstrap.Modal.getOrCreateInstance(this.recipientsModalRef!.nativeElement).show();
    }

    showListMembers(): void {
        if (!this.selectedList) return;
        this.recipientsRunName = this.selectedList.listName;
        this.recipientsData = this.selectedList.users || [];
        bootstrap.Modal.getOrCreateInstance(this.recipientsModalRef!.nativeElement).show();
    }
}
